import * as fns from 'date-fns'
import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'

import { WaitingScope } from 'src/components/waiting/waiting.scopes'
import { MainPresenter } from 'src/main/Main.presenter'
import BorderoKeys from './bordero.keys'
import BorderoScope, { HeaderScope } from './bordero.scopes'
import borderoService from './bordero.service'
import type { BorderoFilterParams } from './bordero.types'
import { BorderoFormKeys } from './form/bordero_form.keys'
import { ResultadoDetalhadoAnaliseKeys } from './resultado-detalhado/resultado_detalhado_analise.keys'
import { StatusProcessamentoKeys } from './status-processamento'

const LOG = Logger.get('BorderoPresenter')

export class BorderoPresenter extends CubePresenter<MainPresenter, BorderoScope> {
    // :: Fields

    private readonly __headerBar: Presenter<HeaderScope, BorderoPresenter>

    // :: Constructor

    public constructor(app: MainPresenter) {
        super(app, new BorderoScope())
        this.__headerBar = new Presenter(this, this.scope.header)
    }

    // :: Methods

    public override release() {
        this.app.releaseToolbar(this.__headerBar)
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new BorderoKeys(this.app, intent)

        this.app.scope.toolbar = this.__headerBar.scope

        keys.parentSlot(this.scope)

        if (initialization) {
            await this.#initialize()
        }

        if (
            initialization ||
            this.scope.filtroDataInicial !== keys.filtroDataInicio ||
            this.scope.filtroDataFinal !== keys.filtroDataFim ||
            this.scope.filtroStatus !== keys.filtroStatus ||
            this.scope.filtroIdBordero !== keys.filtroCdBordero
        ) {
            this.scope.filtroDataInicial = keys.filtroDataInicio ?? fns.subDays(new Date(), 5)
            this.scope.filtroDataFinal = keys.filtroDataFim ?? new Date()
            this.scope.filtroStatus = keys.filtroStatus ?? ''
            this.scope.filtroIdBordero = keys.filtroCdBordero

            await this.handleFind({
                dataInicial: this.scope.filtroDataInicial,
                dataFinal: this.scope.filtroDataFinal,
                status: this.scope.filtroStatus,
                idBordero: this.scope.filtroIdBordero ? Number(this.scope.filtroIdBordero) : undefined
            })
        }

        if (keys.last) {
            keys.dialogSlot(null)
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new BorderoKeys(this.app, intent)

        keys.filtroDataInicio = this.scope.filtroDataInicial
        keys.filtroDataFim = this.scope.filtroDataFinal
        keys.filtroStatus = this.scope.filtroStatus
        keys.filtroCdBordero = this.scope.filtroIdBordero
    }

    async #initialize() {
        LOG.debug('initialize')

        this.scope.onFind = this.action(this.handleFind)
        this.scope.onNew = this.action(this.handleNew)
        this.scope.onReprocess = this.action(this.handleReprocess)
        this.scope.onDelete = this.action(this.handleDelete)
        this.scope.onShowDetailedResults = this.action(this.handleShowDetailedResults)
        this.scope.onShowBorderoProcStatus = this.action(this.handleShowBorderoProcStatus)
    }

    private async handleFind(params: BorderoFilterParams) {
        try {
            this.scope.waiting = new WaitingScope()
            if (!params.dataInicial || !params.dataFinal) {
                throw new Error('Período deve ser informado')
            }
            if (fns.isAfter(params.dataInicial, params.dataFinal)) {
                throw new Error('Data Final deve ser maior que a data inicial')
            }
            if (fns.differenceInDays(params.dataInicial, params.dataFinal) > 31) {
                throw new Error('Período selecionado para filtro deve ser menor ou igual a 1 mês')
            }
            if (params.idBordero && isNaN(params.idBordero)) {
                throw new Error('Código do borderô deve ser um número')
            }

            this.scope.filtroDataInicial = params.dataInicial
            this.scope.filtroDataFinal = params.dataFinal
            this.scope.filtroStatus = params.status
            this.scope.filtroIdBordero = params.idBordero?.toFixed(0)
            const borderos = await borderoService.fetchBorderos(
                params.dataInicial,
                params.dataFinal,
                params.status,
                params.idBordero
            )
            this.scope.borderos = borderos
        } catch (caught) {
            LOG.error('handleFind', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }

    private async handleNew() {
        const target = new BorderoFormKeys(this.app)
        await target.flip()
    }

    private async handleReprocess(id: number) {
        if (!id) {
            throw new Error('Bordero deve ser selecionado')
        }

        try {
            await borderoService.reprocessBordero(id)

            await this.handleFind({
                dataInicial: this.scope.filtroDataInicial,
                dataFinal: this.scope.filtroDataFinal,
                status: this.scope.filtroStatus,
                idBordero: this.scope.filtroIdBordero ? Number(this.scope.filtroIdBordero) : undefined
            })
        } catch (caught) {
            LOG.error('handleReprocess', caught)
            throw caught
        }
    }

    private async handleDelete(id: number) {
        try {
            if (!id) {
                throw new Error('Bordero deve ser selecionado')
            }
            if (!confirm('Confirma a exclusão do borderô?')) {
                return
            }

            this.scope.waiting = new WaitingScope()
            await borderoService.deleteBordero(id)

            this.app.notify('success', 'Borderô excluído com sucesso')

            await this.handleFind({
                dataInicial: this.scope.filtroDataInicial,
                dataFinal: this.scope.filtroDataFinal,
                status: this.scope.filtroStatus,
                idBordero: this.scope.filtroIdBordero ? Number(this.scope.filtroIdBordero) : undefined
            })
        } catch (caught) {
            LOG.error('handleDelete', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }

    private async handleShowDetailedResults(id: number) {
        try {
            if (!id) {
                throw new Error('Bordero deve ser selecionado')
            }
            const keys = new ResultadoDetalhadoAnaliseKeys(this.app)
            keys.borderoId = id
            await keys.flip()
        } catch (caught) {
            LOG.error('handleShowDetailedResults', caught)
            throw caught
        }
    }

    private async handleShowBorderoProcStatus(id: number) {
        try {
            if (!id) {
                throw new Error('Bordero deve ser selecionado')
            }
            const keys = new StatusProcessamentoKeys(this.app)
            keys.borderoId = id
            await keys.flip()
        } catch (caught) {
            LOG.error('handleShowBorderoProcStatus', caught)
            throw caught
        }
    }
}

export default BorderoPresenter
